import React, {useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Popper, Stack, ClickAwayListener, TextField} from '@mui/material';
import {designSystemToken, Icon} from '@lightricks/react-design-system';
import {raiseContentStudioError} from '@/utils/contentStudioErrorHandling';
import translate from '@/utils/translate';
import useRenameAsset from '@/hooks/content-studio/mutations/use-rename-asset';
import useContentStudioStore, {
  contentStudioActions,
  ErrorKind,
} from '@/stores/contentStudioStore';

type RenamePopperProps = {
  anchorElement: HTMLElement | null;
  onClickAway: () => void;
};

function RenamePopper({anchorElement, onClickAway}: RenamePopperProps) {
  const {selectedAssets} = useContentStudioStore(
    useShallow((state) => ({
      selectedAssets: state.selectedAssets,
    }))
  );
  const {mutateAsync: renameAsset} = useRenameAsset();
  const [newAssetName, setNewAssetName] = useState(
    selectedAssets[0]?.fileName?.split('.')[0] || ''
  );
  useEffect(() => {
    setNewAssetName(selectedAssets[0]?.fileName?.split('.')[0] || '');
  }, [selectedAssets]);
  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Popper
        open={!!anchorElement}
        anchorEl={anchorElement}
        placement="top-start"
        modifiers={[
          {
            name: 'offset',
            options: {offset: [0, 30]},
          },
        ]}
        sx={{zIndex: 1000}}
      >
        <Stack
          sx={{
            backgroundColor: designSystemToken('semantic.bg.secondary'),
            borderRadius: '16px',
            boxShadow:
              '0px 5px 26px 4px rgba(0, 0, 0, 0.12), 0px 14px 21px 2px rgba(0, 0, 0, 0.14), 0px 7px 9px -4px rgba(0, 0, 0, 0.20)',
            padding: '8px',
            maxHeight: '300px',
            overflow: 'auto',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap="16px"
            paddingX="16px"
            height="48px"
          >
            <Icon name="Navigation-Edit" size="medium" appearance="primary" />
            <TextField
              placeholder="Rename asset"
              value={newAssetName}
              onChange={(e) => {
                setNewAssetName(e.target.value);
              }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  try {
                    await renameAsset({
                      assetId: selectedAssets[0].id,
                      name: newAssetName,
                    });
                    setNewAssetName('');
                    contentStudioActions.setSelectedAssets([]);
                    onClickAway();
                  } catch (error) {
                    raiseContentStudioError(
                      translate('Failed to rename asset'),
                      ErrorKind.FAILURE
                    );
                  }
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  height: '40px',
                  padding: '0 14px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '16px',
                },
              }}
            />
          </Stack>
        </Stack>
      </Popper>
    </ClickAwayListener>
  );
}

export default RenamePopper;
